import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../store';
import { PermissionSlug } from '../constants/featureFlags';
import { useShallow } from 'zustand/react/shallow';
import {
  getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet,
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
  listBespokeTemplatesBespokeTemplatesGet,
  listBespokeTemplateRevisionsBespokeTemplatesRevisionsGet,
} from '../services/api/sdk.gen';
import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  SectionOrderItem,
  VersionedQuestionSchema,
  BespokeTemplateResponse,
  BespokeTemplateRevision,
} from '../services/api/types.gen';
import type { VisitTypeId } from '../services/api/models/VisitTypeId';
import type { RogerDocFormType } from '../services/api/models/RogerDocFormType';
import { useToast } from '../hooks/useToastContext';
import { fetchRogerEmrFormSchemaVersions } from './formSchema/fetchRogerEmrFormSchemaVersions';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import { ValidationResultsDialog } from './formSchema/ValidationResultsDialog';
import { pickInitialVersion } from './formSchema/pickInitialVersion';
import { ShieldX } from 'lucide-react';
import type { FormTree, FormTreeQuestion } from './formSchema/formSchemaTypes';
import {
  bespokeApiErrorMessage,
  bespokeTemplateRevisionsFingerprint,
  orderedFormTreeSectionIds,
  pinFromFormSchemaVersion,
  templatesBySectionForPin,
  upsertBespokeTemplatesLocal,
} from './formSchema/formSchemaUtils';
import { collectQuestionTreeQids } from '../components/oasis/walkQuestionTree';
import {
  buildLiveConsistencyIndexes,
  SCHEMA_UNKNOWN,
  sectionConsistencyForTemplate,
  type SectionConsistency,
} from './formSchema/sectionConsistency';
import { QuestionsDialog } from './formSchema/QuestionsDialog';
import { BespokeTemplatesImportDialog } from './formSchema/BespokeTemplatesImportDialog';
import { BespokeTemplatesExportDialog } from './formSchema/BespokeTemplatesExportDialog';
import { FormSchemasHeader } from './formSchema/FormSchemasHeader';
import { SchemaVersionBrowser } from './formSchema/SchemaVersionBrowser';
import { FormSchemaStudio } from './formSchema/FormSchemaStudio';
import type { PreviewViewport } from './formSchema/previewViewport';
import { FORM_SCHEMA_QUERY_PARAMS } from './formSchema/formSchemaQueryParams';

/** Poll interval for `/revisions`, which picks up layouts pushed by the MCP. */
const REVISIONS_POLL_MS = 3000;

const FormSchemasWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlStateRef = useRef({
    formType: searchParams.get(
      FORM_SCHEMA_QUERY_PARAMS.formType
    ) as RogerDocFormType | null,
    schemaName: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.schemaName),
    schemaVersion: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.schemaVersion),
    section: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.section),
  });
  /** After first auto-select, only URL params matching the active form type apply. */
  const initialVersionUrlConsumedRef = useRef(false);
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const updateUrlSelection = useCallback(
    (next: {
      formType?: RogerDocFormType | null;
      schemaName?: string | null;
      schemaVersion?: string | null;
      section?: string | null;
    }) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          for (const [field, key] of Object.entries(
            FORM_SCHEMA_QUERY_PARAMS
          ) as [keyof typeof next, string][]) {
            if (!(field in next)) continue;
            const value = next[field];
            if (value == null || value === '') params.delete(key);
            else params.set(key, value);
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [formTypeCatalog, setFormTypeCatalog] = useState<
    RogerDocFormTypeCatalogEntry[]
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [selectedRogerDocFormType, setSelectedRogerDocFormType] =
    useState<RogerDocFormType | null>(null);
  const [versions, setVersions] = useState<FormSchemaVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  const [selectedVersion, setSelectedVersion] =
    useState<FormSchemaVersion | null>(null);
  // Lives in the workspace (not FormSchemaStudio): changing form type clears
  // selectedVersion and remounts the studio; keep the phone/tablet/print
  // preview choice across that transition.
  const [previewViewport, setPreviewViewport] =
    useState<PreviewViewport>('laptop');
  const [formTree, setFormTree] = useState<FormTree | null>(null);
  // The FSV's `section_order`, so the sidebar uses the same order and labels
  // the visit UI does. `null` when the FSV doesn't ship one.
  const [formTreeSectionOrder, setFormTreeSectionOrder] = useState<
    SectionOrderItem[] | null
  >(null);
  const [treeLoading, setTreeLoading] = useState(false);

  const [templates, setTemplates] = useState<BespokeTemplateResponse[]>([]);
  const [templatesLoadFailed, setTemplatesLoadFailed] = useState(false);
  // Read by the poll, which must not restart its interval when this flips.
  const templatesLoadFailedRef = useRef(false);

  const selectedCatalogEntry = useMemo(
    () =>
      formTypeCatalog.find(r => r.form_type === selectedRogerDocFormType) ??
      null,
    [formTypeCatalog, selectedRogerDocFormType]
  );

  const canonicalVisitForApi: VisitTypeId | null =
    selectedCatalogEntry?.canonical_visit_type_id ?? null;

  const [questionDialog, setQuestionDialog] = useState<{
    sectionName: string;
    questions: FormTreeQuestion[];
  } | null>(null);
  const [selectedSectionName, setSelectedSectionName] = useState<string | null>(
    null
  );

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [validationResultsOpen, setValidationResultsOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const getCanonicalVisitForFormType = useCallback(
    (formType: RogerDocFormType | null | undefined): VisitTypeId | null =>
      formTypeCatalog.find(row => row.form_type === formType)
        ?.canonical_visit_type_id ?? null,
    [formTypeCatalog]
  );

  const fetchVersions = useCallback(async (visitTypeApi: VisitTypeId) => {
    setVersionsLoading(true);
    setVersionsError(null);
    setSelectedVersion(null);
    setFormTree(null);
    setFormTreeSectionOrder(null);
    try {
      setVersions(await fetchRogerEmrFormSchemaVersions(visitTypeApi));
    } catch (err) {
      console.error('Failed to fetch schema versions:', err);
      setVersions([]);
      setVersionsError(
        bespokeApiErrorMessage(err) ||
          'Could not load schema versions for this form type.'
      );
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setCatalogLoading(true);
      try {
        const res = await getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet(
          {}
        );
        const rows =
          (res?.data as RogerDocFormTypeCatalogEntry[] | undefined) ?? [];
        if (cancelled) return;
        setFormTypeCatalog(rows);
        setSelectedRogerDocFormType(prev => {
          if (prev != null && rows.some(r => r.form_type === prev)) {
            return prev;
          }
          const urlFormType = initialUrlStateRef.current.formType;
          if (
            urlFormType != null &&
            rows.some(r => r.form_type === urlFormType)
          ) {
            return urlFormType;
          }
          return rows[0]?.form_type ?? null;
        });
      } catch (err) {
        console.error('Failed to load Roger Doc form type catalog:', err);
        if (!cancelled) {
          setFormTypeCatalog([]);
          setSelectedRogerDocFormType(null);
          toastRef.current.error(
            'Catalog unavailable',
            'Could not load Roger Doc form types. Refresh to retry.'
          );
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (canonicalVisitForApi == null) return;
    void fetchVersions(canonicalVisitForApi);
  }, [canonicalVisitForApi, fetchVersions]);

  /** `section_id → display_name` for the active form tree. */
  const formTreeLabelByCategory = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const item of formTreeSectionOrder ?? []) {
      lookup[item.section_id] = item.display_name;
    }
    return lookup;
  }, [formTreeSectionOrder]);

  const orderedFormTreeSections = useMemo<string[]>(() => {
    if (!formTree) return [];
    return orderedFormTreeSectionIds(formTree, formTreeSectionOrder);
  }, [formTree, formTreeSectionOrder]);

  useEffect(() => {
    if (!formTree) {
      setSelectedSectionName(null);
      return;
    }

    const sections = orderedFormTreeSections;
    setSelectedSectionName(prev =>
      initialUrlStateRef.current.section &&
      sections.includes(initialUrlStateRef.current.section)
        ? initialUrlStateRef.current.section
        : prev && sections.includes(prev)
          ? prev
          : (sections[0] ?? null)
    );
  }, [formTree, orderedFormTreeSections]);

  // The build endpoint returns {version, schema_data} for a (name, version)
  // selector: the form tree, plus section_order for order and labels. Only the
  // latest request writes the tree, so a slow response for a version the user
  // already left cannot land over the current one.
  const formTreeRequestRef = useRef(0);
  const fetchFormTree = useCallback(
    async (version: FormSchemaVersion) => {
      const visitTypeApi =
        getCanonicalVisitForFormType(version.form_type as RogerDocFormType) ??
        canonicalVisitForApi;
      if (visitTypeApi == null) return;
      const request = ++formTreeRequestRef.current;
      const isLatest = () => request === formTreeRequestRef.current;
      setTreeLoading(true);
      try {
        const res =
          await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
            query: { visit_type: visitTypeApi },
            body: {
              schema_version: {
                name: version.name,
                version: version.version ?? null,
              },
              // Forms Manager shows the same question set the MCP sees.
              omit_inline_goals: true,
            },
          });
        if (!isLatest()) return;
        const payload = res?.data as VersionedQuestionSchema | undefined;
        setFormTree((payload?.schema_data as FormTree | undefined) ?? null);
        setFormTreeSectionOrder(payload?.version.section_order ?? null);
      } catch (err) {
        if (!isLatest()) return;
        console.error('Failed to fetch form tree:', err);
        setFormTree(null);
        setFormTreeSectionOrder(null);
      } finally {
        if (isLatest()) setTreeLoading(false);
      }
    },
    [canonicalVisitForApi, getCanonicalVisitForFormType]
  );

  // The list is every layout in the environment, not one pin's: validation
  // and consistency compare across forms.
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await listBespokeTemplatesBespokeTemplatesGet({
        throwOnError: true,
      });
      setTemplates((res?.data as BespokeTemplateResponse[] | undefined) ?? []);
      templatesLoadFailedRef.current = false;
      setTemplatesLoadFailed(false);
    } catch (err) {
      // Keep the previous list: an emptied catalog would read as no layouts.
      console.error('Failed to fetch bespoke templates:', err);
      templatesLoadFailedRef.current = true;
      setTemplatesLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (selectedVersion || versionsLoading || versions.length === 0) return;
    if (
      selectedRogerDocFormType != null &&
      versions.some(v => v.form_type !== selectedRogerDocFormType)
    ) {
      return;
    }

    const urlMatchesFormType =
      !initialVersionUrlConsumedRef.current &&
      initialUrlStateRef.current.formType === selectedRogerDocFormType;
    const { schemaName, schemaVersion } = urlMatchesFormType
      ? initialUrlStateRef.current
      : { schemaName: null, schemaVersion: null };
    const match = pickInitialVersion(versions, { schemaName, schemaVersion });
    if (!match) return;
    initialVersionUrlConsumedRef.current = true;
    setSelectedVersion(match);
    void fetchFormTree(match);
  }, [
    fetchFormTree,
    selectedRogerDocFormType,
    selectedVersion,
    versions,
    versionsLoading,
  ]);

  const handleVersionClick = (v: FormSchemaVersion) => {
    setSelectedVersion(v);
    setSelectedSectionName(null);
    updateUrlSelection({
      formType: v.form_type ? (v.form_type as RogerDocFormType) : null,
      schemaName: v.name,
      schemaVersion: `${v.version ?? ''}`,
      section: null,
    });
    void fetchFormTree(v);
  };

  const handleSectionClick = (sectionName: string) => {
    setSelectedSectionName(sectionName);
    updateUrlSelection({ section: sectionName });
  };

  const handleFormTypeChange = (nextFormType: RogerDocFormType) => {
    setSelectedRogerDocFormType(nextFormType);
    setVersions([]);
    setVersionsError(null);
    setVersionsLoading(true);
    setSelectedVersion(null);
    setSelectedSectionName(null);
    setFormTree(null);
    setFormTreeSectionOrder(null);
    setTreeLoading(false);
    initialVersionUrlConsumedRef.current = true;
    initialUrlStateRef.current = {
      formType: nextFormType,
      schemaName: null,
      schemaVersion: null,
      section: null,
    };
    updateUrlSelection({
      formType: nextFormType,
      schemaName: null,
      schemaVersion: null,
      section: null,
    });
  };

  /** Full RogerDoc pin for the selected form type + schema version. */
  const selectedPin = useMemo(() => {
    if (!selectedVersion || !selectedRogerDocFormType) return null;
    return pinFromFormSchemaVersion(selectedRogerDocFormType, selectedVersion);
  }, [selectedRogerDocFormType, selectedVersion]);

  /** The one layout per section for the selected pin. */
  const templateBySection = useMemo(() => {
    if (!selectedPin) return new Map<string, BespokeTemplateResponse>();
    return templatesBySectionForPin(templates, selectedPin);
  }, [selectedPin, templates]);

  /** Cross-form design consistency index; memoised because it walks the catalog. */
  const consistencyIndexes = useMemo(
    () => buildLiveConsistencyIndexes(templates),
    [templates]
  );

  const getSectionConsistency = useCallback(
    (section: string): SectionConsistency =>
      sectionConsistencyForTemplate(
        templateBySection.get(section),
        consistencyIndexes,
        // Before the tree loads the schema is unknown, which is not the same
        // as a section with no questions: an empty set would hide findings.
        formTree
          ? new Set(collectQuestionTreeQids(formTree[section] ?? []))
          : SCHEMA_UNKNOWN
      ),
    [consistencyIndexes, templateBySection, formTree]
  );

  // Layouts change only through the MCP, so poll the lightweight revisions
  // endpoint and refetch the pin's rows when one appears or moves.
  useEffect(() => {
    if (!selectedVersion || !selectedRogerDocFormType) return;

    let cancelled = false;
    let fingerprint: string | null = null;
    const query = {
      form_type: selectedRogerDocFormType,
      schema_version_name: selectedVersion.name,
      form_schema_version: selectedVersion.version,
    };

    const poll = async () => {
      // A failed full load leaves the catalog stale, so reload all of it.
      if (templatesLoadFailedRef.current) {
        await fetchTemplates();
        return;
      }
      try {
        const res =
          await listBespokeTemplateRevisionsBespokeTemplatesRevisionsGet({
            query,
            throwOnError: true,
          });
        const revisions =
          (res?.data as BespokeTemplateRevision[] | undefined) ?? [];
        const next = bespokeTemplateRevisionsFingerprint(revisions);
        if (cancelled || next === fingerprint) return;
        const list = await listBespokeTemplatesBespokeTemplatesGet({
          query,
          throwOnError: true,
        });
        if (cancelled) return;
        const rows =
          (list?.data as BespokeTemplateResponse[] | undefined) ?? [];
        setTemplates(prev => upsertBespokeTemplatesLocal(prev, rows));
        // Only after a successful refetch, so a failed one retries next tick.
        fingerprint = next;
      } catch (err) {
        // Silent by design: a background poll should not toast.
        console.error('Failed to refresh bespoke templates:', err);
      }
    };

    const runPollIfVisible = () => {
      if (!document.hidden) void poll();
    };
    const intervalId = setInterval(runPollIfVisible, REVISIONS_POLL_MS);
    runPollIfVisible();
    document.addEventListener('visibilitychange', runPollIfVisible);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', runPollIfVisible);
    };
  }, [fetchTemplates, selectedRogerDocFormType, selectedVersion]);

  const handleCopyLayout = useCallback(
    async (template: BespokeTemplateResponse | null) => {
      if (!template?.layout_json) {
        toast.error('Nothing to copy', 'This section has no layout.');
        return;
      }
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(template.layout_json, null, 2)
        );
        toast.success('Copied', 'Roger Layout Schema copied to clipboard.');
      } catch {
        toast.error('Clipboard failed', 'Could not write to clipboard.');
      }
    },
    [toast]
  );

  const validationSeedVersionsByFormType = useMemo(() => {
    if (!selectedRogerDocFormType || versions.length === 0) return undefined;
    return new Map([[selectedRogerDocFormType, versions]]);
  }, [selectedRogerDocFormType, versions]);

  return (
    <div className='space-y-6'>
      <div className='space-y-4'>
        <SchemaVersionBrowser
          actions={
            <FormSchemasHeader
              catalogLoading={catalogLoading}
              catalogEmpty={formTypeCatalog.length === 0}
              onImport={() => setImportModalOpen(true)}
              onExport={() => setExportModalOpen(true)}
              onOpenValidation={() => setValidationResultsOpen(true)}
            />
          }
          formTypeCatalog={formTypeCatalog}
          catalogLoading={catalogLoading}
          selectedRogerDocFormType={selectedRogerDocFormType}
          onFormTypeChange={handleFormTypeChange}
          versions={versions}
          versionsLoading={versionsLoading}
          versionsError={versionsError}
          selectedVersion={selectedVersion}
          onVersionSelect={handleVersionClick}
        />

        {templatesLoadFailed && (
          <div className='flex items-center gap-3 text-sm text-destructive'>
            <span>Could not load layouts.</span>
            <Button
              variant='outline'
              size='sm'
              onClick={() => void fetchTemplates()}
            >
              Retry
            </Button>
          </div>
        )}

        {selectedVersion &&
          selectedRogerDocFormType &&
          selectedVersion.form_type === selectedRogerDocFormType && (
            <FormSchemaStudio
              selectedRogerDocFormType={selectedRogerDocFormType}
              selectedVersion={selectedVersion}
              selectedSectionName={selectedSectionName}
              formTree={formTree}
              formTreeLabelByCategory={formTreeLabelByCategory}
              orderedFormTreeSections={orderedFormTreeSections}
              treeLoading={treeLoading}
              templateBySection={templateBySection}
              getSectionConsistency={getSectionConsistency}
              onSelectSection={handleSectionClick}
              onCopyLayout={template => void handleCopyLayout(template)}
              onViewQuestions={(section, questions) =>
                setQuestionDialog({ sectionName: section, questions })
              }
              previewViewport={previewViewport}
              onPreviewViewportChange={setPreviewViewport}
            />
          )}
      </div>

      <BespokeTemplatesImportDialog
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        formTypeCatalog={formTypeCatalog}
        catalogLoading={catalogLoading}
        onImported={() => void fetchTemplates()}
      />

      <BespokeTemplatesExportDialog
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        formTypeCatalog={formTypeCatalog}
        catalogLoading={catalogLoading}
      />

      <QuestionsDialog
        sectionName={questionDialog?.sectionName ?? null}
        questions={questionDialog?.questions ?? []}
        onClose={() => setQuestionDialog(null)}
      />

      <ValidationResultsDialog
        open={validationResultsOpen}
        onOpenChange={setValidationResultsOpen}
        templates={templates}
        formTypeCatalog={formTypeCatalog}
        seedVersionsByFormType={validationSeedVersionsByFormType}
      />
    </div>
  );
};

/**
 * Permission gate for the Forms Manager workspace.
 *
 * Forms Manager is Roger-employee tooling and its API is super-user only;
 * Gating on `emr:manage` would render the page for an agency admin
 * whose every request the backend then refuses. The check lives out here rather
 * than inside the workspace so a denied visitor never mounts it: the workspace
 * fetches the catalog and templates on mount and polls `/revisions` every three
 * seconds, and a guard that returns after those hooks are declared still runs
 * them, turning an Access Denied card into a standing stream of 403s.
 */
const FormSchemas: React.FC = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );

  if (!hasPermission(PermissionSlug.ADMIN_SUPER_USER)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-destructive'>
            <ShieldX className='h-5 w-5' />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-muted-foreground'>
            You don&apos;t have permission to access Form Schema management.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <FormSchemasWorkspace />;
};

export default FormSchemas;
